import { defaultNodeState } from 'dashboard/lib/stubs/graph';
import { AccountType } from 'dashboard/models/account-type';
import {
  DatumTypeToken,
  DatumValueType,
  Directionality,
  Edge,
  GraphId,
  GraphPropertyType,
  Node,
  NodeDataTypeIcon,
  NodeId,
  PropertySource,
} from 'dashboard/models/graph';
import { ServerHydratedNode, ServerLightlyHydratedNode } from 'dashboard/models/graph-hydration';
import {
  AccountsQuery,
  ClustersQuery,
  Creator,
  GraphQuery,
  NHopsFromAccountQuery,
  ParameterMap,
} from 'dashboard/models/graph-query';
import { FieldSchema } from 'lib/fields';

// --------------------------------------------------------------------------
// Graph V2 server response types
// --------------------------------------------------------------------------

export interface ServerNode {
  'datum-type': DatumTypeToken;
  value: string;
}

export interface ServerEdge {
  node1: ServerNode;
  node2: ServerNode;
}

export interface ServerNodeExpandability {
  node: ServerNode;
  'nonfetched-edge-count': number;
}

export type ServerQueryCursor = string;

export interface ServerNodesAndEdges {
  nodes: ServerNode[];
  edges: ServerEdge[];
}

export interface ServerColorRuleStatus {
  type: 'status';
  status: string;
  color: string;
}

export interface ServerColorRuleTag {
  type: 'tag';
  tag: string;
  color: string;
}

export interface ServerColorRuleCondition {
  type: 'condition';
  condition: string;
  color: string;
}

export type ServerColorRule = ServerColorRuleStatus | ServerColorRuleTag | ServerColorRuleCondition;

export type ServerCluster = ServerNodesAndEdges;

export interface ServerNodeDatumType {
  'datum-type-token': DatumTypeToken;
  name: string;
  'value-type': DatumValueType;
}

export interface ServerGraphProperty {
  source: PropertySource;
  value: string;
}

// --------------------------------------------------------------------------
// START - Graph V2 Node Functions Configs
// --------------------------------------------------------------------------

// PresetType should mirror those defined in app/lib/graph/node_functions_manager.rb
export enum PresetType {
  NormalizeAddress = 'normalize_address',
  NormalizeDate = 'normalize_date',
  NormalizeDateTime = 'normalize_date_time',
  NormalizeDecimal = 'normalize_decimal',
  NormalizeEmail = 'normalize_email',
  NormalizeGovernmentId = 'normalize_government_id',
  NormalizeName = 'normalize_name',
  NormalizeIdDocumentNumber = 'normalize_id_document_number',
  NormalizeIpAddress = 'normalize_ip_address',
  NormalizePhoneNumber = 'normalize_phone_number',
  NormalizePostalCode = 'normalize_postal_code',
  NormalizeSimilarityTargets = 'normalize_similarity_targets',
  NormalizeSocialSecurityNumber = 'normalize_social_security_number',
  ValidateAccount = 'validate_account',
  ValidateBoolean = 'validate_boolean',
  ValidateInteger = 'validate_integer',
  ValidateSocialSecurityNumberLast4 = 'validate_social_security_number_last_4',
  ValidateString = 'validate_string',
  ValidateUuid = 'validate_uuid',
  ValidateBooleanFalse = 'validate_boolean_false',
  RunRegex = 'regex',
}

export interface ParamMapping {
  path: string;
  destination: string;
  'function-chain-input-token'?: string;
  'direct-value'?: string;
}

export interface FunctionMapping {
  token: string;
  'param-mappings': ParamMapping[];
}

export enum SourceObjectType {
  Account = 'Account',
  Inquiry = 'Inquiry',
  Network = 'Network',
  Device = 'Device',
  Document = 'Document',
}

export interface NodeKeyEntry {
  key: string;
  'function-token': string;
  path: string;
}

export interface NodeFunctionsSource {
  'object-type-token': string;
  'object-type': SourceObjectType;
  'function-mappings': FunctionMapping[];
  path: string;
  'node-key': NodeKeyEntry[];
}

export interface ServerPresetSummary {
  preset: PresetType;
  // TODO @graph - FIX THIS
  'param-schema': Record<string, any>[];
  'direct-params': string[];
  'output-schema': FieldSchema;
}

export interface NodeFunction {
  token: string;
  preset: PresetType;
}

export interface ServerNodeFunctionsConfig {
  'datum-type-token': DatumTypeToken;
  disabled: boolean;
  sources: NodeFunctionsSource[];
  functions: NodeFunction[];
  'node-key-schema': FieldSchema[];
}

export interface ServerSearchableField {
  'left-value': string;
  'display-name': string;
}

export const getSourceName = (source: NodeFunctionsSource, accountType?: AccountType): string => {
  if (source['object-type'] === 'Account' && accountType) {
    return source.path
      ? `${accountType.attributes.name}.${source.path}`
      : `${accountType.attributes.name}`;
  } else {
    return source['object-type'];
  }
};

// --------------------------------------------------------------------------
// END - Graph V2 Node Function Config
// --------------------------------------------------------------------------

export interface ServerNodeRenderConfig {
  'datum-type-token': DatumTypeToken;
  'display-name': string;
  color: string;
  icon: NodeDataTypeIcon;
  'search-placeholder': string;
  'context-menu': Array<ServerGraphProperty>;
}

export interface ServerPropertyConfig {
  'datum-type-token': DatumTypeToken;
  'display-name': string;
  indexed: boolean;
  'property-source': string;
  'property-value': string;
  'property-type': GraphPropertyType;
}

export interface ServerAccountsResponse {
  version: 'graph_v2';
  nodes: ServerNode[];
  edges: ServerEdge[];
  'expandable-nodes': ServerNodeExpandability[];
  'next-cursor': ServerQueryCursor;
}

export interface ServerClusterResponse {
  version: 'graph_v2';
  nodes: ServerNode[];
  edges: ServerEdge[];
  'expandable-nodes': ServerNodeExpandability[];
  'next-cursor': ServerQueryCursor;
}

export interface ServerNHopsResponse {
  version: 'graph_v2';
  nodes: ServerNode[];
  edges: ServerEdge[];
  'expandable-nodes': ServerNodeExpandability[];
  'next-cursor': ServerQueryCursor;
  'seed-set': ServerNode[];
}

export interface ServerExpandResponse {
  version: 'graph_v2';
  'nodes-and-edges': ServerNodesAndEdges;
  'expandable-nodes': ServerNodeExpandability[];
}

export interface ServerHydrateResponse {
  version: 'graph_v2';
  'hydrated-nodes': ServerHydratedNode[];
}

export interface ServerLightlyHydrateResponse {
  version: 'graph_v2';
  // null is used when there's no additional hydration data for light hydration.
  'lightly-hydrated-nodes': (ServerLightlyHydratedNode | null)[];
}

export const convertServerNodeToNodeId = (serverNode: ServerNode): NodeId => {
  return {
    token: serverNode['datum-type'],
    value: serverNode.value,
  };
};

export const convertServerNodeToNode = (serverNode: ServerNode): Node => {
  return {
    id: convertServerNodeToNodeId(serverNode),
    state: defaultNodeState(),
    hoverCards: new Set<HTMLDivElement>(),
  };
};

export const convertServerEdgeToEdge = (serverEdge: ServerEdge): Edge => {
  return {
    id1: convertServerNodeToNodeId(serverEdge.node1),
    id2: convertServerNodeToNodeId(serverEdge.node2),
    directionality: Directionality.Bidirectional,
  };
};

// --------------------------------------------------------------------------
// Graph V2 interface for talking to the backend (or faking backend data
// with a headless version)
// --------------------------------------------------------------------------

// Note: The return values are promises, since they're meant to be async.
export interface Backend {
  accounts(params: {
    query: AccountsQuery;
    graphId: GraphId;
    creatorId: Creator['id'];
    creatorType: Creator['type'];
  }): Promise<GraphQuery>;

  cluster(params: {
    query: ClustersQuery;
    graphId: GraphId;
    creatorId: Creator['id'];
    creatorType: Creator['type'];
  }): Promise<GraphQuery>;

  liveQuery(graphId: string): Promise<GraphQuery>;

  nHopsFromAccount(params: {
    query: NHopsFromAccountQuery;
    graphId: GraphId;
    creatorId: Creator['id'];
    creatorType: Creator['type'];
  }): Promise<GraphQuery>;

  queryWithTemplate(params: {
    graphQueryTemplateId: string;
    parameterMap: ParameterMap;
    graphId: string;
    creatorId: Creator['id'];
    creatorType: Creator['type'];
  }): Promise<GraphQuery>;

  queryWithTemplateAndPoll(params: {
    graphQueryTemplateId: string;
    parameterMap: ParameterMap;
    graphId: string;
    creatorId: Creator['id'];
    creatorType: Creator['type'];
    shouldContinue: () => boolean;
    onNetworkError?: (e: Error) => void;
    onQueryError?: (query: GraphQuery) => void;
    onQueryPolled?: (query: GraphQuery) => void;
  }): Promise<GraphQuery | null>;

  poll(params: { graphQueryId: string }): Promise<GraphQuery>;
  pollContinuously(params: {
    graphQueryId: string;
    shouldContinue: () => boolean;
    onError?: (e: Error) => void;
    onNetworkError?: (e: Error) => void;
    onQueryError?: (query: GraphQuery) => void;
    // callback for when query is polled, and query is not completed
    onQueryPolled?: (query: GraphQuery) => void;
    cancelQueryIfStoppedPolling?: boolean;
  }): Promise<GraphQuery | null>;
  runQueryAndPoll(params: {
    query: AccountsQuery | ClustersQuery | NHopsFromAccountQuery;
    graphId: string;
    creatorId: Creator['id'];
    creatorType: Creator['type'];
    shouldContinue: () => boolean;
    onError?: (e: Error) => void;
    onNetworkError?: (e: Error) => void;
    onQueryError?: (query: GraphQuery) => void;
    onQuerySubmitted?: (query: GraphQuery) => void;
    onQueryPolled?: (query: GraphQuery) => void;
  }): Promise<GraphQuery | null>;
  customDatumTypesOverride(): ServerNodeDatumType[] | null;

  nodeRenderConfigOverride(): ServerNodeRenderConfig[] | null;

  propertyConfigOverride(): ServerPropertyConfig[] | null;

  coloringRulesOverride(): ServerColorRule[] | null;

  expand(params: {
    nodeId: NodeId;
    graphId: GraphId;
    expandDatumTypes: DatumTypeToken[];
    existingNodeIds: NodeId[];
  }): Promise<ServerExpandResponse>;

  hydrate(params: {
    nodeIds: NodeId[];
    graphId: GraphId;
    includeAccountFields?: boolean;
  }): Promise<ServerHydrateResponse>;

  lightlyHydrate(params: {
    nodeIds: NodeId[];
    graphId: GraphId;
  }): Promise<ServerLightlyHydrateResponse>;
}
